import { classifyRenewalFailure, renewalRetryDelayMs } from './renewalPolicy.js';

// Decides what a token renewal outcome means for the mounted session.
// - success: keep going (and stop any pending retry);
// - definitive failure (Keycloak refused the refresh token, or there is none): end the session;
// - transient failure (network, timeout, 5xx): keep the session and retry with backoff;
// - a failure that another path has already made irrelevant (a newer, unexpired token,
//   or a different login) changes nothing.
//
// readSession() returns the current session as
// { generation, accessToken, expiresAtMs, renewable } or null.
// renew() performs the (single-flight) renewal and resolves with the renewed session.
export function createRenewalCoordinator({ renew, readSession, endSession, timers = {}, now = () => Date.now() }) {
  // Called as plain functions: browsers throw "Illegal invocation" for window.setTimeout
  // invoked as a method of another object.
  const setTimer = timers.setTimeout || ((callback, ms) => setTimeout(callback, ms));
  const clearTimer = timers.clearTimeout || ((handle) => clearTimeout(handle));
  let retryTimer = null;
  let retryAttempt = 0;

  const isFresh = (session) => !!session && !(Number.isFinite(session.expiresAtMs) && session.expiresAtMs <= now());

  function cancelRetry() {
    if (retryTimer !== null) clearTimer(retryTimer);
    retryTimer = null;
    retryAttempt = 0;
  }

  function scheduleRetry() {
    if (retryTimer !== null) return;
    retryTimer = setTimer(() => {
      retryTimer = null;
      void renewNow().catch(() => {});
    }, renewalRetryDelayMs(retryAttempt));
    retryAttempt += 1;
  }

  // Returns 'ignored' | 'superseded' | 'ended' | 'retrying'.
  function settleFailure(error, startedWith) {
    const current = readSession();
    if (!current || !startedWith || current.generation !== startedWith.generation) return 'ignored';
    if (current.accessToken !== startedWith.accessToken && isFresh(current)) return 'superseded';
    if (classifyRenewalFailure(error, { renewable: current.renewable }) === 'definitive') {
      cancelRetry();
      endSession();
      return 'ended';
    }
    scheduleRetry();
    return 'retrying';
  }

  async function renewNow() {
    const startedWith = readSession();
    if (!startedWith) throw Object.assign(new Error('There is no session to renew.'), { code: 'NO_SESSION' });
    if (!startedWith.renewable) {
      const error = Object.assign(new Error('The session has no refresh token.'), { code: 'NO_REFRESH_TOKEN' });
      settleFailure(error, startedWith);
      throw error;
    }
    try {
      const renewed = await renew();
      cancelRetry();
      return renewed;
    } catch (error) {
      settleFailure(error, startedWith);
      throw error;
    }
  }

  return Object.freeze({
    renew: renewNow,
    // The access token's lifetime ran out: renew first, end only on a definitive failure.
    accessTokenExpired() {
      const current = readSession();
      if (!current || isFresh(current)) return Promise.resolve(null);
      return renewNow().catch(() => null);
    },
    // oidc-client-ts's automatic renewal failed.
    renewalFailed(error) {
      return settleFailure(error, readSession());
    },
    // The API rejected `rejected` ({ generation, accessToken }): renew unless a newer token already exists.
    async renewAfterRejection(rejected) {
      const hasNewerToken = (session) => !!session && session.generation === rejected.generation
        && session.accessToken !== rejected.accessToken && isFresh(session);
      const current = readSession();
      if (!current || !rejected || current.generation !== rejected.generation) return current;
      if (hasNewerToken(current)) return current;
      try {
        return await renewNow();
      } catch (error) {
        const latest = readSession();
        if (hasNewerToken(latest)) return latest;
        throw error;
      }
    },
    // A fresh token arrived or the session ended: nothing left to retry.
    settled: cancelRetry,
    retryPending: () => retryTimer !== null,
  });
}
